import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ExportButtons from "../../reports/components/ExportButtons";
import type { ReportTableColumn } from "../../reports/components/ReportPointsTable";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { fetchQuickDaily } from "../quickReportsService";
import HosClocks, { QUICK_TIMEOUT_MS } from "./HosClocks";
import type { SegmentState } from "../types";
import {
  BITACORA_TITLE,
  FILE_COLUMNS,
  STATE_COLOR,
  STATE_LABEL,
  clampDay,
  dayBar,
  ESTIMATED_STYLE,
  segmentLabel,
  driverCard,
  eventChips,
  fileBase,
  fileRows,
  fmtDMY,
  fmtHM,
  fmtJornada,
  fmtLongDay,
  indicators,
  isEmptyDay,
  pickDay,
  segmentRow,
  shiftDay,
  todayIn,
  userTz,
  type FileRow,
} from "./bitacora";

interface Props {
  deviceId: string;
  unitName: string;
  isDarkMode: boolean;
  companyId?: string | null;
  // Día con el que abre (por omisión, hoy). Nunca un día futuro.
  initialDate?: string;
  // Informes rápidos ya avisa "Datos de ejemplo" arriba; el panel del mapa no.
  mockNotice?: boolean;
  // Relojes de horas de servicio a un lado (Informes rápidos); el panel del
  // mapa es angosto y no los lleva.
  hos?: boolean;
  // "Asignar conductor" cuando no hay: el mapa abre su acción Conductor; sin
  // callback lleva a Administración → Conductores; null lo oculta.
  onAssignDriver?: (() => void) | null;
}

const COLUMNS: ReportTableColumn<FileRow>[] = FILE_COLUMNS.map((c) => ({ key: c.key, header: c.header }));

const ICON: Record<SegmentState, string> = {
  driving: "ri-steering-2-line",
  maneuver: "ri-arrow-left-right-line",
  idle: "ri-temp-hot-line",
  stopped: "ri-pause-line",
  rest: "ri-zzz-line",
  no_data: "ri-signal-wifi-off-line",
};

// Números grandes y unidades chicas, como los indicadores de Mapon ("29.3 km").
function Figure({ text, dark }: { text: string; dark: boolean }) {
  if (text === "—") return <span className={dark ? "text-gray-500" : "text-gray-400"}>—</span>;
  return (
    <>
      {text.split(/(\d[\d.,]*)/).map((part, i) =>
        /^\d/.test(part) ? (
          <span key={i} className="text-lg font-semibold tabular-nums">
            {part}
          </span>
        ) : (
          <span key={i} className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>
            {part}
          </span>
        ),
      )}
    </>
  );
}

// Bitácora de jornada: una unidad, un día (ESPECIFICACION.txt §1.1). La misma
// pieza vive en Informes rápidos y en la tarjeta de la unidad del mapa.
export default function BitacoraJornada({ deviceId, unitName, isDarkMode, companyId, initialDate, mockNotice = true, hos = false, onAssignDriver }: Props) {
  const navigate = useNavigate();
  const tz = useMemo(userTz, []);
  const today = todayIn(tz);
  const [date, setDate] = useState(() => clampDay(initialDate ?? today, today));
  const storeCompany = useCompaniesStore((s) => s.companyId);
  const company = companyId ?? storeCompany ?? (typeof localStorage !== "undefined" ? localStorage.getItem("userCompanyId") : null);

  // La fecha va en la llave: si cambia mientras carga, react-query cancela la
  // petición vieja (signal) y pickDay además ignora cualquier día que no sea éste.
  // Plazo de 15 s y sin reintento automático: con el retry global (1) y el
  // timeout de axios (30 s) el esqueleto podía durar más de un minuto; ahora
  // pasa a "Reintentar" y la persona decide.
  const query = useQuery({
    queryKey: ["quick-reports", "bitacora", company, deviceId, date, tz],
    queryFn: ({ signal }) => fetchQuickDaily({ companyId: company!, from: date, to: date, tz, deviceIds: [deviceId] }, signal, QUICK_TIMEOUT_MS),
    enabled: !!company && !!deviceId,
    staleTime: 60_000,
    retry: false,
  });
  const day = pickDay(query.data?.items, deviceId, date);
  const rows = useMemo(() => (day ? fileRows(day, tz) : []), [day, tz]);
  const bar = useMemo(() => (day ? dayBar(day.segments) : []), [day]);
  const presentStates = useMemo(() => [...new Set(bar.map((b) => b.state))], [bar]);

  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const strong = dark ? "text-white" : "text-gray-900";
  const line = dark ? "border-dark-border" : "border-gray-200";
  const field = dark ? "bg-dark-surface border-dark-border text-white" : "bg-white border-gray-300 text-gray-900";
  const square = `w-9 h-9 flex items-center justify-center rounded-md border transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${field} ${
    dark ? "hover:bg-white/5" : "hover:bg-gray-50"
  }`;
  const driver = driverCard(day?.driver ?? null);

  const body = () => {
    if (!company) return <p className={`py-10 text-sm text-center ${muted}`}>Selecciona una compañía.</p>;
    if (query.isLoading)
      return (
        <div className="flex flex-col gap-3 py-2" aria-busy="true" aria-label="Cargando bitácora">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={`h-10 rounded-lg animate-pulse ${dark ? "bg-white/5" : "bg-black/5"}`} style={{ animationDelay: `${i * 90}ms` }} />
          ))}
        </div>
      );
    if (query.isError)
      return (
        <div className="py-10 text-center">
          <p className={`text-sm ${muted}`}>No se pudo cargar la bitácora.</p>
          <button type="button" onClick={() => query.refetch()} className="mt-3 px-4 py-1.5 rounded-lg text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white">
            Reintentar
          </button>
        </div>
      );
    if (isEmptyDay(day))
      return (
        <div className="py-12 text-center">
          <i className={`ri-signal-wifi-off-line text-3xl ${dark ? "text-gray-600" : "text-gray-300"}`} aria-hidden />
          <p className={`mt-2 text-sm ${muted}`}>La unidad no reportó nada este día.</p>
        </div>
      );
    const d = day!;
    const chips = eventChips(d.event_counts);
    return (
      <>
        {/* 3. Conductor */}
        <section className={`flex items-center gap-3 py-3 border-b ${line}`} aria-label="Conductor">
          {driver ? (
            <>
              {driver.photo ? (
                <img src={driver.photo} alt="" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <span className="w-11 h-11 rounded-full bg-primary-500/15 text-primary-500 flex items-center justify-center text-sm font-semibold" aria-hidden>
                  {driver.initials}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold truncate ${strong}`}>{driver.name}</p>
                <p className={`text-xs ${muted}`}>{[driver.license && `Lic. ${driver.license}`, driver.expires].filter(Boolean).join(" · ") || "Sin licencia registrada"}</p>
              </div>
              {driver.shift && (
                <span className={`text-xs tabular-nums px-2 py-1 rounded-md ${dark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`} title="Turno">
                  {driver.shift}
                </span>
              )}
            </>
          ) : (
            <>
              <span className={`w-11 h-11 rounded-full flex items-center justify-center ${dark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"}`} aria-hidden>
                <i className="ri-user-line text-lg" />
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${muted}`}>Sin conductor asignado</p>
                {onAssignDriver !== null && (
                  <button
                    type="button"
                    onClick={onAssignDriver ?? (() => navigate("/admin/drivers"))}
                    className={`mt-0.5 inline-flex items-center gap-1 text-sm font-medium ${dark ? "text-primary-300" : "text-primary-600"} hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded`}
                  >
                    <i className="ri-user-add-line" aria-hidden />
                    Asignar conductor
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        {/* 4. Indicadores */}
        <dl className={`grid grid-cols-3 gap-y-4 py-4 border-b ${line}`}>
          {indicators(d).map((x) => (
            <div key={x.key} className="flex flex-col-reverse text-center px-1">
              <dt className={`text-xs mt-0.5 ${muted}`}>{x.label}</dt>
              <dd className={`leading-tight ${strong}`}>
                <Figure text={x.value} dark={dark} />
              </dd>
            </div>
          ))}
        </dl>

        {/* 5. Barra del día */}
        <section className={`py-4 border-b ${line}`} aria-label="Barra del día">
          <div className={`flex h-3 w-full rounded-full overflow-hidden ${dark ? "bg-white/5" : "bg-gray-100"}`}>
            {bar.map((b, i) => (
              <span
                key={i}
                className={`h-full ${STATE_COLOR[b.state][dark ? "dark" : "light"]}`}
                style={{ width: `${b.pct}%`, ...(b.estimated ? ESTIMATED_STYLE : {}) }}
                title={`${segmentLabel(b)} · ${fmtHM(b.start_ts, tz)} – ${fmtHM(b.end_ts, tz)}`}
              />
            ))}
          </div>
          <div className={`flex justify-between mt-1 text-[11px] tabular-nums ${muted}`}>
            <span>{bar[0] ? fmtHM(bar[0].start_ts, tz) : ""}</span>
            <span>{bar.length ? fmtHM(bar[bar.length - 1].end_ts, tz) : ""}</span>
          </div>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {presentStates.map((s) => (
              <li key={s} className={`inline-flex items-center gap-1.5 text-xs ${muted}`}>
                <span className={`w-2.5 h-2.5 rounded-sm ${STATE_COLOR[s][dark ? "dark" : "light"]}`} aria-hidden />
                {STATE_LABEL[s]}
              </li>
            ))}
            {bar.some((b) => b.estimated) && (
              <li className={`inline-flex items-center gap-1.5 text-xs ${muted}`} title="Hueco sin reportes en el que la unidad avanzó a ritmo de manejo">
                <span className={`w-2.5 h-2.5 rounded-sm ${STATE_COLOR.driving[dark ? "dark" : "light"]}`} style={ESTIMATED_STYLE} aria-hidden />
                Manejo estimado
              </li>
            )}
          </ul>
        </section>

        {/* 6. Eventos (sólo los mayores a 0) */}
        {chips.length > 0 && (
          <section className={`flex flex-wrap gap-2 py-3 border-b ${line}`} aria-label="Eventos">
            {chips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-amber-500/15 text-amber-600">
                {c.label}
                <span className="tabular-nums font-semibold">{c.count}</span>
              </span>
            ))}
          </section>
        )}

        {/* 7. Tramos: línea de tiempo como el panel Rutas de Mapon */}
        <ol className="relative py-2" aria-label="Tramos del día">
          {d.segments.map((s, i) => {
            const r = segmentRow(s, tz);
            return (
              <li key={i} className="relative flex items-start gap-3 py-2">
                {/* Línea punteada que une los tramos */}
                {i < d.segments.length - 1 && (
                  <span aria-hidden className={`absolute left-[11px] top-8 -bottom-2 border-l border-dashed ${dark ? "border-gray-600" : "border-gray-300"}`} />
                )}
                <span
                  aria-hidden
                  style={s.estimated ? ESTIMATED_STYLE : undefined}
                  className={`relative z-[1] mt-0.5 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${STATE_COLOR[s.state][dark ? "dark" : "light"]} ${
                    s.state === "no_data" ? (dark ? "text-gray-300" : "text-gray-500") : "text-white"
                  }`}
                >
                  <i className={`${ICON[s.state]} text-xs`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${strong}`}>
                    <span className="font-medium">{r.title}</span>
                    <span className={`ml-2 tabular-nums ${muted}`}>{r.range}</span>
                  </p>
                  <p className={`text-xs mt-0.5 flex min-w-0 ${muted}`}>
                    <span className="flex-shrink-0">{r.detail}</span>
                    {r.address && (
                      <span className="truncate" title={r.address}>
                        &nbsp;· {r.address}
                      </span>
                    )}
                  </p>
                </div>
                {r.max && <span className={`text-xs tabular-nums whitespace-nowrap mt-0.5 ${s.max_speed_kmh > 90 ? "text-amber-500 font-semibold" : muted}`}>{r.max}</span>}
              </li>
            );
          })}
        </ol>
        <p className={`pt-2 text-[11px] ${muted}`}>
          {d.segments.length} tramos · conduciendo {fmtJornada(d.driving_s)} · maniobra {fmtJornada(d.maneuver_s)} · detenido {fmtJornada(d.stopped_s)}
          {d.no_data_s ? ` · sin datos ${fmtJornada(d.no_data_s)}` : ""}
        </p>
      </>
    );
  };

  return (
    <div className="flex flex-col">
      {mockNotice && query.data?.mock && (
        <p role="status" className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
          <strong className="font-semibold">Datos de ejemplo.</strong> El servicio todavía no está disponible.
        </p>
      )}
      {/* 1-2. Fecha con < > y descargas */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`bitacora-date-${deviceId}`}>
          Fecha
        </label>
        <input
          id={`bitacora-date-${deviceId}`}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(clampDay(e.target.value, today))}
          className={`h-9 rounded-md border px-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 ${field} ${dark ? "[color-scheme:dark]" : ""}`}
        />
        <button type="button" className={square} onClick={() => setDate((d) => shiftDay(d, -1, today))} aria-label="Día anterior" title="Día anterior">
          <i className="ri-arrow-left-s-line text-lg" aria-hidden />
        </button>
        <button type="button" className={square} onClick={() => setDate((d) => shiftDay(d, 1, today))} disabled={date >= today} aria-label="Día siguiente" title="Día siguiente">
          <i className="ri-arrow-right-s-line text-lg" aria-hidden />
        </button>
        <div className="flex-1" />
        <ExportButtons rows={rows} columns={COLUMNS} meta={{ title: BITACORA_TITLE, period: `${unitName} · ${fmtDMY(date)}`, fileBase: fileBase(unitName, date) }} />
      </div>
      <p className={`mt-2 text-xs first-letter:uppercase ${muted}`}>
        {fmtLongDay(date)}
        {date === today ? " · hoy hasta ahora" : ""}
      </p>
      {/* key: el día nuevo entra con un fundido corto. */}
      <div key={date} className="motion-safe:animate-fadeIn">
        {hos && company && day && !query.isError && !isEmptyDay(day) ? (
          // Móvil: relojes arriba en una fila. Escritorio: columna a la derecha.
          <div className="flex flex-col xl:flex-row-reverse xl:items-start gap-4 xl:gap-8 mt-3">
            <HosClocks day={day} companyId={company} date={date} tz={tz} isToday={date === today} isDarkMode={dark} />
            <div className="min-w-0 flex-1">{body()}</div>
          </div>
        ) : (
          body()
        )}
      </div>
    </div>
  );
}
